import { get, post, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'
import {
  clearSession,
  getStoredUser,
  hasSession,
  setSession,
  setStoredUser,
} from '@/services/authToken'
import { toDateFormatKey, toDateFormatWire, toTimeFormatKey } from '@/lib/orgFormats'

/**
 * Sign-in against the real authentication API.
 *
 * The credential travels in the body over the same interceptor chain as
 * everything else — never in a query string, where it would land in browser
 * history, proxy logs and the referrer header.
 *
 * The API is tenant-scoped: the account alias selects the organisation, and an
 * email is unique only within one, so the same address can exist under two
 * accounts and mean two different people. All three fields are required.
 *
 * ── Two calls, not one ──────────────────────────────────────────────────
 * `POST /auth/login` returns tokens and nothing else. It used to describe the
 * user as well; it no longer does, so the profile comes from `GET /auth/me`
 * immediately afterwards. The order is forced rather than chosen: `/auth/me` is
 * authenticated, and the request interceptor reads the access token out of the
 * cookie store, so the tokens have to be persisted before the call is made.
 */

/**
 * The wire shape is snake_case, the app is camelCase. Converted at this seam.
 *
 * `name` is composed rather than served: the API sends the parts, and every
 * consumer — the top bar, the rail's initials, the greeting — wants them joined.
 * Composing once here beats four call sites each doing it slightly differently.
 * It is null, not an empty string, when there is nothing to join, because the
 * greeting's ICU select tests for a name and `''` would read as one.
 *
 * Note what is NOT here: no id and no email. `/auth/me` serves neither, and
 * inventing either would present a guess as fact. See BACKEND-GAPS.md.
 */
function toUser(user) {
  if (!user) return null

  const firstName = (user.first_name ?? '').trim() || null
  const lastName = (user.last_name ?? '').trim() || null
  const name = [firstName, lastName].filter(Boolean).join(' ') || null

  return {
    firstName,
    lastName,
    name,
    avatarUrl: user.avatar_view_url ?? null,
    timezone: user.timezone ?? null,
    dateFormat: toDateFormatKey(user.date_format),
    timeFormat: toTimeFormatKey(user.time_format),
    // Whether this account is the tenant's root user. Display only — what a root
    // user may actually do is the API's ruling, made per request.
    isRoot: user.is_root === true,
  }
}

/** The organisation the session belongs to. */
function toAccount(account) {
  if (!account) return null
  return {
    name: (account.org_name ?? '').trim() || null,
    logoUrl: account.logo_view_url ?? null,
  }
}

/**
 * Who is signed in.
 *
 * Returns the whole profile, not just the person: the account, the roster shifts
 * and the permission map travel on the same response and belong to the same
 * session, so splitting them across separate query keys would just mean three
 * cache entries that can disagree about which session they describe.
 *
 * `permissions` is `{key: [scope, …]}` and is what `features/auth/permissions.js`
 * reads to hide what the API will refuse. A client-side check is not
 * authorisation in any case — the API re-decides on every request.
 */
export async function fetchMe({ signal } = {}) {
  const payload = await get(endpoints.auth.me, { signal })

  return {
    user: toUser(payload?.user),
    account: toAccount(payload?.account),
    shifts: Array.isArray(payload?.shifts) ? payload.shifts : [],
    permissions: payload?.permissions ?? {},
  }
}

/**
 * @param {{accountAlias: string, email: string, password: string}} credential
 */
export async function signIn(credential) {
  const payload = await post(endpoints.auth.login, {
    // Trimmed because a copy-pasted alias or email routinely carries whitespace,
    // and rejecting someone over an invisible character is cruel. NOT
    // upper-cased: the API compares the alias exactly, so changing its case
    // would fail every sign-in.
    account_alias: credential.accountAlias.trim(),
    email: credential.email.trim(),
    // The password is never trimmed — a trailing space can be deliberate.
    password: credential.password,
  })

  const tokens = {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
  }

  // Tokens first, profile second — `/auth/me` cannot be called before the access
  // token is somewhere the request interceptor will find it. `user` is omitted
  // rather than nulled so a reload racing this line still finds the last profile.
  setSession(tokens)

  try {
    const profile = await fetchMe()
    setStoredUser(profile)
    return { ...tokens, ...profile }
  } catch (error) {
    /**
     * Fail the sign-in rather than returning half of one.
     *
     * A session whose profile never arrived has no name for the top bar, no
     * account, and an empty permission map that would read as "permitted
     * nothing". Landing someone in the app in that state looks like the app is
     * broken; failing here shows them the sign-in error they can act on. The
     * tokens just written are cleared so nothing is left half-signed-in.
     */
    clearSession()
    throw error
  }
}

/**
 * The profile record, as `GET /users/me` serves it.
 *
 * A different shape from `toUser` above, and a different endpoint: this one
 * carries the `id`, the `email` and the `avatar_file_id` that the session read
 * does not. The form needs all three — the id to be sure which row it is
 * editing, the email to show what signing in currently uses, and the file id
 * because the update must send it back unchanged when the picture is not being
 * touched.
 */
function toProfileRecord(payload) {
  if (!payload) return null
  const firstName = (payload.first_name ?? '').trim() || null
  const lastName = (payload.last_name ?? '').trim() || null

  return {
    id: payload.id ?? null,
    firstName,
    lastName,
    name: [firstName, lastName].filter(Boolean).join(' ') || null,
    email: payload.email ?? null,
    avatarFileId: payload.avatar_file_id ?? null,
    avatarUrl: payload.avatar_view_url ?? null,
    timezone: payload.timezone ?? null,
    dateFormat: toDateFormatKey(payload.date_format),
    timeFormat: toTimeFormatKey(payload.time_format),
  }
}

/**
 * The time zones the API will accept.
 *
 * Fetched rather than derived from `Intl`, because the two are not the same set
 * and the server's is the one that decides. It validates against
 * `zoneinfo.available_timezones()` on ITS runtime, which depends on the tzdata
 * that runtime was built with; the browser's `supportedValuesOf` holds canonical
 * identifiers only. `Asia/Calcutta` is in one and not the other, and a zone
 * offered from the wrong list is refused on save with a 422 that names a field
 * the person did not knowingly touch.
 */
export async function fetchTimezones({ signal } = {}) {
  const payload = await get(endpoints.account.timezones, { signal })
  return Array.isArray(payload?.timezones) ? payload.timezones : []
}

/**
 * Turn a stored zone into one the list actually contains.
 *
 * A record can hold a deprecated alias — `Asia/Calcutta` for `Asia/Kolkata` —
 * either because it was seeded that way or because the browser reported it at
 * sign-up. Left alone it breaks the form twice over: the select cannot match it
 * so it renders blank, and saving sends it straight back to a validator that
 * refuses it. Nobody typed it, so nobody knows what to fix.
 *
 * `Intl` does the mapping rather than a hand-written table of aliases: asking
 * for a zone and reading back what was resolved is the platform's own
 * canonicalisation, and it stays right as the database changes. The result is
 * used only if the server's list agrees.
 *
 * Returns null when nothing can be reconciled, which the form states rather than
 * quietly substituting a zone the person never chose. A wrong time zone is not
 * cosmetic here — it is what every shift boundary and timestamp is read against.
 */
export function canonicalTimezone(zone, allowed) {
  if (!zone) return null
  if (allowed.includes(zone)) return zone

  try {
    const resolved = Intl.DateTimeFormat('en', { timeZone: zone }).resolvedOptions().timeZone
    if (resolved && allowed.includes(resolved)) return resolved
  } catch {
    /* Not a zone this browser knows either. Falls through to null. */
  }
  return null
}

/** The editable record behind the profile form. */
export async function fetchProfile({ signal } = {}) {
  return toProfileRecord(await get(endpoints.users.me, { signal }))
}

/**
 * Update the signed-in user's own profile.
 *
 * ── Every field, every time ─────────────────────────────────────────────
 * `PUT /users/me`, and `UserMeUpdate` gives none of its fields a default — so
 * all nine must be present on every request, including the three that are
 * nullable. A field left out is a 422, not a field left alone. That is why the
 * form seeds its draft from the record it is about to overwrite, and why
 * `avatarFileId` is passed through untouched when the picture is not being
 * changed: omitting it would not mean "keep it", it would mean "reject this".
 *
 * ── The password pair is how the server authenticates the change ────────
 * `current_password` is required when the email OR the password changes, and
 * null otherwise. The server checks it before either. Sending a new password
 * without the current one is refused, so the form asks for both together.
 *
 * ── Changing the email does not change the email ────────────────────────
 * It sends a confirmation link to the NEW address and leaves the record alone
 * until that link is opened. The response says so in its message, and the form
 * says so on screen — otherwise somebody signs out and cannot get back in with
 * the address they think they just set.
 *
 * @param {object} draft
 * @param {string} draft.firstName
 * @param {string} draft.lastName
 * @param {string} draft.email
 * @param {string|null} draft.timezone
 * @param {string|null} draft.dateFormat   App vocabulary (`dmy`), translated here.
 * @param {string|null} draft.timeFormat
 * @param {string|null} [draft.avatarFileId]
 *   A confirmed file id to attach, `null` to detach the current picture, or
 *   `undefined` to send back whatever is already on the record.
 * @param {string|null} [draft.currentPassword]
 * @param {string|null} [draft.newPassword]
 */
export async function updateProfile(draft) {
  const body = {
    first_name: draft.firstName.trim(),
    last_name: draft.lastName.trim(),
    email: draft.email.trim(),
    timezone: draft.timezone ?? null,
    date_format: toDateFormatWire(draft.dateFormat),
    time_format: draft.timeFormat ?? null,
    // `undefined` is "unchanged", which on a full-replace PUT means resending
    // what is already there rather than omitting the key.
    avatar_file_id: draft.avatarFileId === undefined ? (draft.currentAvatarFileId ?? null) : draft.avatarFileId,
    new_password: draft.newPassword || null,
    current_password: draft.currentPassword || null,
  }

  const record = toProfileRecord(await put(endpoints.users.me, body))

  /**
   * The session is re-read rather than patched from this response.
   *
   * `/users/me` answers with the profile row; the shell renders from
   * `/auth/me`, which is a different shape and also carries the account and the
   * permission map. Deriving one from the other would mean assembling a session
   * out of a record that does not contain all of it, so the authoritative read
   * is repeated instead. It is one request, on an action taken once in a while.
   */
  const profile = await fetchMe()
  setStoredUser(profile)
  return { record, profile }
}

/**
 * Sign out.
 *
 * Entirely client-side, because there is nothing to call: the API exposes no
 * logout endpoint, and its refresh tokens are stateless JWTs with no server-side
 * record behind them, so nothing can revoke one. A request would be theatre.
 *
 * The consequence is worth stating plainly: a refresh token captured before
 * sign-out stays valid until it expires. Only the server can change that.
 */
/**
 * Ask for a password reset link.
 *
 * ── Nothing about the outcome comes back, on purpose ────────────────────
 * The API answers the same 200 with the same message whether the account
 * exists, the address exists under it, or neither does. That is correct — an
 * endpoint that said "no such address" would be an oracle for confirming which
 * addresses have accounts on this platform, from outside the login wall.
 *
 * So this returns nothing, and the screen over it must claim nothing: its
 * success state says a link has been sent IF an account matches, which is the
 * only true statement available. A caller tempted to branch on the response has
 * nothing to branch on, which is the point.
 *
 * The one thing it does NOT swallow is a transport failure. "We could not reach
 * the platform" is about this request rather than about any account, so it
 * reveals nothing and is worth saying — otherwise a dropped connection presents
 * as a link that is never going to arrive.
 *
 * The mail carries a `set_password` token, the same type an invitation does, so
 * the link lands on the same screen an invited user sees.
 *
 * @param {{accountAlias: string, email: string}} input
 */
export async function requestPasswordReset({ accountAlias, email }) {
  await post(endpoints.auth.forgotPassword, {
    // Trimmed, and the alias NOT case-folded — the same two rules sign-in
    // follows, and for the same reason: the API compares the alias exactly.
    account_alias: accountAlias.trim(),
    email: email.trim(),
  })
  return { requested: true }
}

export async function signOut() {
  clearSession()
}

/**
 * The session as it was last persisted, read synchronously.
 *
 * This is the CACHED profile, not a fresh one — it exists so the first paint
 * after a reload has a name in it. `useSession` seeds itself from here and then
 * re-reads `/auth/me`, which is the authority.
 *
 * Returns null when there is no session, which is what the route guard checks.
 * A token being present is not proof that it is valid — only the API can rule on
 * that, and it does so by rejecting the first request that carries one.
 */
const EMPTY_PROFILE = { user: null, account: null, shifts: [], permissions: {} }

export function readSession() {
  if (!hasSession()) return null

  /**
   * What is stored is the whole `/auth/me` profile. It used to be the bare user
   * object from the login payload, so a browser that signed in before this
   * change still holds the older shape — and read as a profile, that yields
   * `user: undefined` and a top bar with no name until the fetch lands.
   *
   * Checked rather than migrated: the fetch is about to overwrite it anyway, so
   * the only thing the old value can still do is seed one wrong frame.
   */
  const stored = getStoredUser()
  const looksLikeProfile = stored !== null && typeof stored === 'object' && 'user' in stored

  return looksLikeProfile ? stored : EMPTY_PROFILE
}
